import React, { useContext } from "react";
import { Card, Text, BlockStack, InlineStack, Box, Bleed, Divider } from "@shopify/polaris";

import baseHelper from "lib/helpers/base";
import constant from "lib/constant/constant";
import { PrivateContext } from "lib/context";

import { fullfillmentStatusProp } from "app/orders/modules/operator/features/view/propsType";

import { TotalSection } from "app/orders/modules/operator/features/view/genericStyled";
import { StyledFulfillment } from "asset/styles/globalStyle";

const FulfillmentStatus = (props) => {
  const { cms, userData } = useContext(PrivateContext);
  const { data, lineData, fulfilledData, totalFulfilledAmount = 0, totalRejectAmount = 0, term } = props;

  const isRejected =
    lineData &&
    lineData.every(
      (item) =>
        item.status === constant.REJECTED &&
        (item.fulfillmentStatus === constant.UNFULFILLED || item.fulfillmentStatus === constant.REFUND)
    );
  const currentUserData = baseHelper.getResponseData(userData, constant.gql.GET_CURRENT_USER);
  const isSeller = baseHelper.isSeller(currentUserData);
  const isTaxIncluded = !!data.taxesIncluded;

  const totalTax = lineData.reduce((prev, value) => {
    return prev + (isTaxIncluded ? value.actualQuantity * parseFloat(value.totalTax) : parseFloat(value.totalTax));
  }, 0);

  const rejectedData = {
    commissionSeller: 0,
    commissionVendor: 0,
    discount: 0,
    isUpdatedOrderLine: true,
    markUp: 0,
    markUpItems: 0,
    price: 0,
    sellerDiscount: 0,
    shippingAmount: 0,
    tax: 0,
    totalTaxRate: 0,
    totalPrice: 0,
  };

  const totals = isRejected
    ? rejectedData
    : baseHelper.getOrderPrice(lineData, data, isSeller, false, false, fulfilledData);
  const { isUpdatedOrderLine, price = 0 } = totals || {};
  const subTotal = parseFloat(price);
  const updatedLineSubTotal = parseFloat(data.totalLineItemsPrice);

  const { code: discountLineCode = "", amount } =
    (data.discountCodes && data.discountCodes.length && data.discountCodes[0]) || {};
  const lineDataItems = lineData.filter(
    (lineItem) =>
      !(
        lineItem.status === constant.REJECTED &&
        (lineItem.fulfillmentStatus === constant.UNFULFILLED || lineItem.fulfillmentStatus === constant.REFUND)
      )
  ).length;
  const items = `${lineDataItems > constant.value.ONE ? constant.items : `${cms("label.items")}:`} ${lineDataItems}`;
  const markUpItems = `${totals.markUpItems > constant.value.ONE ? constant.items : `${cms("label.items")}:`} ${
    totals.markUpItems
  }`;
  const moneyFormat = data.moneyFormat || constant.symbol.DOLLAR;

  const { title: shippingLineTitle = "" } =
    (data.shippingLines && data.shippingLines.length && data.shippingLines[0]) || {};

  const { price: shippingLinePrice = "" } =
    (data.shippingLines && data.shippingLines.length && data.shippingLines[0]) || {};

  const vendorsSum = [];
  lineData.filter(({ vendor }) => {
    vendorsSum.push(vendor);
    return vendorsSum;
  });
  const vendorSet = new Set(vendorsSum);
  const setSize = vendorSet.size;
  const addVendorTxt =
    setSize === constant.value.ONE ? `${setSize} ${cms("label.vendor")}` : `${setSize} ${cms("label.vendors")}`;

  return (
    <Box padding="400">
      <BlockStack gap="400">
        <InlineStack wrap={false} align="space-between">
          <Text as="h5">{`${cms("label.subTotal")} (Exclude rejected order line item)`}</Text>
          <BlockStack inlineAlign="end" gap="100">
            <Text as="span">{items}</Text>
            <Text as="span">
              {moneyFormat}
              {isUpdatedOrderLine
                ? Number(baseHelper.getPrice(subTotal)).toLocaleString()
                : Number(updatedLineSubTotal.toFixed(2)).toLocaleString()}
            </Text>
          </BlockStack>
        </InlineStack>
        <Divider />
        {isUpdatedOrderLine && !!totals.markUp && (
          <>
            <InlineStack wrap={false} align="space-between">
              <Text as="h5">{cms("label.markUpTotal")}</Text>
              <BlockStack inlineAlign="end" gap="100">
                <Text as="span">{markUpItems}</Text>
                <Text as="span">
                  {`${moneyFormat}`}
                  {baseHelper.getPrice(totals.markUp)}
                </Text>
              </BlockStack>
            </InlineStack>
            <Divider />
          </>
        )}
        <InlineStack wrap={false} align="space-between">
          <div className="ellipsis">
            {cms("label.shipping")}
            {`${shippingLineTitle ? `(${shippingLineTitle})` : ""}`}
          </div>
          <Text as="span">
            {moneyFormat}
            {`${
              term === "flat" || !totals.shippingAmount
                ? baseHelper.getPrice(shippingLinePrice)
                : baseHelper.getPrice(totals.shippingAmount)
            }`}
          </Text>
        </InlineStack>
        <Divider />
        <InlineStack wrap={false} align="space-between">
          <Text as="h5">{cms("label.marketplaceDiscount")}</Text>
          <Text as="h5">
            {`${moneyFormat}-`}
            {Number(baseHelper.getPrice(totals.sellerDiscount))}
          </Text>
        </InlineStack>
        {!!totals.marketplaceDiscount && (
          <>
            <Divider />
            <InlineStack wrap={false} align="space-between">
              <Text as="h5">
                {cms("label.discount")}
                {discountLineCode ? `(${discountLineCode})` : ""}
              </Text>
              <Text as="span">
                {`${moneyFormat}-`}
                {baseHelper.getPrice(totals.marketplaceDiscount) || constant.value.FLOAT_ZERO}
              </Text>
            </InlineStack>
          </>
        )}
        <Divider />
        <InlineStack wrap={false} align="space-between">
          <Text as="h5">{`${cms("label.salesTax")} ${(isTaxIncluded && cms("label.included")) || ""}`}</Text>
          <Text as="h5">
            {moneyFormat}
            {isSeller && isUpdatedOrderLine && Number(totalTax).toLocaleString()}
          </Text>
        </InlineStack>
      </BlockStack>
      <br />
      <Bleed marginInline="400" marginBlockEnd="400">
        <Box borderColor="border" borderWidth="025" padding="400" background="">
          <InlineStack wrap={false} blockAlign={constant.CENTER} align="space-between">
            <b>{cms("label.total")}</b>
            <BlockStack inlineAlign="end">
              <Text variant="bodySm" as="p">
                {" "}
                (
                {lineDataItems > 1
                  ? `${lineDataItems}
                ${cms("label.items")} from ${addVendorTxt}`
                  : `${lineDataItems}
                ${cms("label.item")} from ${addVendorTxt}`}
                )
              </Text>
              <Text as="span" fontWeight="semibold">
                {moneyFormat}
                {term === "flat" || !isUpdatedOrderLine
                  ? Number(baseHelper.getPrice(data.totalPrice)).toLocaleString()
                  : Number(totals.totalPrice).toLocaleString()}
              </Text>
            </BlockStack>
          </InlineStack>
        </Box>
      </Bleed>
    </Box>
  );
};

FulfillmentStatus.propTypes = fullfillmentStatusProp.type;
export default FulfillmentStatus;
